import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { IconChevronDown, IconDots } from '@tabler/icons-react'
import {
  HeadingResItem,
  getTestByHeading,
  useGetHeadings,
} from '@/services/heading/useGetTestByHeading'
import { Meta } from '@/types/common'
import {
  CreatorType,
  formatPracticeTestStatusTypeLabel,
  PracticeTestList,
  PracticeTestStatus,
  PracticeTestType,
} from '@/types/practice-test'
import copy from 'copy-to-clipboard'
import { useParticipantStore } from '@/stores/participantStore'
import { cn } from '@/lib/utils'
import { STUDENT_SITE_URL } from '@/utils/constants'
import {
  getCreatorLabel,
  getMarksPercentage,
  getMarksStyle,
} from '@/utils/style'
import { FormatFullDateMonthYearHourMinute } from '@/utils/time'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import Pagination from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const TESTS_PAGE_LIMIT = 10

function HeadingGroupRows({
  heading,
  classId,
  moduleId,
  tests,
  isLoadingTests,
  isExpanded,
  onToggle,
  showLoadMore,
  onLoadMore,
  isLoadingMore,
  showStatusColumn = false,
  isPractice,
}: {
  heading: HeadingResItem
  classId: string
  moduleId: string
  tests: PracticeTestList[]
  isLoadingTests: boolean
  isExpanded: boolean
  onToggle: () => void
  showLoadMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
  showStatusColumn?: boolean
  isPractice: boolean
}) {
  const navigate = useNavigate()
  const { updateExamTestData } = useParticipantStore()
  const colSpan = 7 + (showStatusColumn ? 1 : 0)

  return (
    <>
      <TableRow className='bg-[#F9FAFB]'>
        <TableCell colSpan={colSpan} className='p-0'>
          <button
            type='button'
            className='font-mundial flex w-full items-center gap-2 px-[24px] py-[10px] text-left text-base font-bold text-[#002652] capitalize hover:bg-[#F2F4F7]'
            onClick={onToggle}
            aria-expanded={isExpanded}
          >
            <IconChevronDown
              className={cn(
                'size-5 shrink-0 text-[#475467] transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
              aria-hidden
            />
            <span>{heading.name}</span>
          </button>
        </TableCell>
      </TableRow>

      {isExpanded && isLoadingTests ? (
        <TableRow>
          <TableCell
            colSpan={colSpan}
            className='px-[24px] py-[12px] text-sm text-[#475467]'
          >
            Loading...
          </TableCell>
        </TableRow>
      ) : null}

      {isExpanded && !isLoadingTests
        ? tests.map((item) => (
            <TableRow key={item.id} className='hover:bg-gray-50'>
              <TableCell className='px-[24px] py-[12px]'>
                <div>
                  <div className='truncate text-sm font-[400] text-[#002652]'>
                    {item.name}
                  </div>
                  <div className='truncate text-sm font-[300] text-[#475467]'>
                    Created date:{' '}
                    {FormatFullDateMonthYearHourMinute(item.created_at)}
                  </div>
                </div>
              </TableCell>
              <TableCell className='text-center text-sm font-[300] text-[#002652]'>
                {item.participants ?? 'N/A'}
              </TableCell>
              <TableCell className='text-center text-sm font-[300] text-[#002652]'>
                {item.unmarked ?? 'N/A'}
              </TableCell>
              <TableCell
                className={cn(
                  'truncate text-center text-sm font-[300] text-[#002652]',
                  getMarksStyle(item.avg_ai_marks)
                )}
              >
                {getMarksPercentage(item.avg_ai_marks)}
              </TableCell>
              <TableCell
                className={cn(
                  'truncate text-center text-sm font-[300] text-[#002652]',
                  getMarksStyle(item.avg_tutor_marks)
                )}
              >
                {getMarksPercentage(item.avg_tutor_marks)}
              </TableCell>
              <TableCell className='truncate text-sm font-[300] text-[#002652] capitalize'>
                {getCreatorLabel(item.creator_type)}
              </TableCell>
              {showStatusColumn ? (
                <TableCell className='text-center text-sm font-[300] capitalize text-[#002652]'>
                  {formatPracticeTestStatusTypeLabel(item.status_type)}
                </TableCell>
              ) : null}
              <TableCell className='w-[5%] px-3 text-center'>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='link'
                      className='h-auto rounded px-2 py-1 font-medium text-[#0057B8] hover:bg-[#dbe9fa]'
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <IconDots />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align='end'
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    {isPractice ? (
                      <>
                        {String(item.creator_type ?? '').toLocaleLowerCase() !==
                          PracticeTestType.STUDENT && (
                          <DropdownMenuItem
                            onClick={() => {
                              navigate({
                                to: '/create-practice-test/detail/$ptId/$moduleId',
                                params: { ptId: item.id.toString(), moduleId },
                                search: { source: 'classDetailPracticeTest' },
                              })
                            }}
                          >
                            View details
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            updateExamTestData(item)
                            navigate({
                              to: '/marking/list-participant/$ptId/$classId',
                              params: { ptId: item.id.toString(), classId },
                              search: { source: 'classDetailPracticeTest' },
                            })
                          }}
                        >
                          {String(item.creator_type ?? '').toLocaleLowerCase() !==
                          PracticeTestType.STUDENT
                            ? 'View participants'
                            : 'View'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            copy(
                              `${STUDENT_SITE_URL}/my-class/detail/${classId}?test_id=${item.id}`
                            )
                          }}
                        >
                          Copy test link
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem
                          onClick={() => {
                            navigate({
                              to: '/create-mock-exam/detail/$ptId/$moduleId',
                              params: { ptId: item.id.toString(), moduleId },
                              search: { source: 'classDetailMockExam' },
                            })
                          }}
                        >
                          View details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            updateExamTestData(item)
                            navigate({
                              to: '/marking/list-participant/$ptId/$classId',
                              params: { ptId: item.id.toString(), classId },
                              search: { source: 'classDetailMockExam' },
                            })
                          }}
                        >
                          View participants
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))
        : null}

      {isExpanded && !isLoadingTests && showLoadMore && onLoadMore ? (
        <TableRow>
          <TableCell colSpan={colSpan} className='px-[24px] py-3 text-center'>
            <Button
              type='button'
              variant='outline'
              className='min-w-[120px] border-[#D0D5DD] text-[#002652]'
              disabled={isLoadingMore}
              onClick={onLoadMore}
            >
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </TableCell>
        </TableRow>
      ) : null}

      {isExpanded && !isLoadingTests && tests.length === 0 ? (
        <TableRow>
          <TableCell
            colSpan={colSpan}
            className='px-[24px] py-[12px] text-sm text-[#475467]'
          >
            {isPractice
              ? 'No practice tests in this heading.'
              : 'No mock exams in this heading.'}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

function HeadingGroupSection({
  heading,
  classId,
  moduleId,
  isExpanded,
  onToggle,
  status,
  search,
  creatorType,
  showStatusColumn = false,
  isPractice,
}: {
  heading: HeadingResItem
  classId: string
  moduleId: string
  isExpanded: boolean
  onToggle: () => void
  status: PracticeTestStatus | null
  search: string
  creatorType: CreatorType | null
  showStatusColumn?: boolean
  isPractice: boolean
}) {
  const { data, fetchNextPage, hasNextPage, isPending, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'testByHeading',
        String(heading.id),
        TESTS_PAGE_LIMIT,
        status ?? 'all',
        search,
        creatorType ?? null,
        isPractice,
      ],
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        getTestByHeading({
          headingId: String(heading.id),
          page: pageParam as number,
          limit: TESTS_PAGE_LIMIT,
          status,
          search,
          creator_type: creatorType ?? null,
          isPractice,
        }),
      getNextPageParam: (lastPage) => {
        const meta = lastPage?.meta as Meta | undefined
        if (!meta) return undefined
        const current = Number(meta.current_page ?? 1)
        const total = Number(meta.total_pages ?? 1)
        return current < total ? current + 1 : undefined
      },
      enabled: isExpanded,
    })

  const tests = useMemo(
    () =>
      data?.pages.flatMap((p) => (Array.isArray(p?.data) ? p.data : [])) ?? [],
    [data?.pages]
  )

  return (
    <HeadingGroupRows
      heading={heading}
      classId={classId}
      moduleId={moduleId}
      tests={tests}
      isLoadingTests={Boolean(isExpanded && isPending)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      showLoadMore={Boolean(hasNextPage)}
      onLoadMore={() => fetchNextPage()}
      isLoadingMore={isFetchingNextPage}
      showStatusColumn={showStatusColumn}
      isPractice={isPractice}
    />
  )
}

export function GroupedByHeadingTable({
  moduleId,
  classId,
  isHeadingEnabled,
  creatorType,
  search,
  status,
  showStatusColumn = false,
  isPractice,
}: {
  moduleId: string
  classId: string
  search: string
  creatorType: CreatorType | null
  isHeadingEnabled: boolean
  status: PracticeTestStatus | null
  showStatusColumn?: boolean
  isPractice: boolean
}) {
  const [page, setPage] = useState(1)

  const { headings, isLoadingHeadings, meta } = useGetHeadings({
    classId,
    enabled: isHeadingEnabled,
    page: 1,
    limit: 10,
    isPractice,
  })

  const [expandedHeadingIds, setExpandedHeadingIds] = useState<Set<number>>(
    () => new Set()
  )

  const headingsIdsKey = useMemo(
    () => headings?.map((h: HeadingResItem) => h.id).join(',') ?? '',
    [headings]
  )

  useEffect(() => {
    if (!headingsIdsKey) return
    setExpandedHeadingIds(
      new Set(
        headingsIdsKey
          .split(',')
          .map((id: string) => Number(id))
          .filter((id: number) => !Number.isNaN(id))
      )
    )
  }, [headingsIdsKey])

  const handleToggleHeading = useCallback((headingId: number) => {
    setExpandedHeadingIds((prev) => {
      const next = new Set(prev)
      if (next.has(headingId)) {
        next.delete(headingId)
      } else {
        next.add(headingId)
      }
      return next
    })
  }, [])

  if (isLoadingHeadings) {
    return (
      <div className='px-[24px] py-[16px] text-sm text-[#475467]'>
        Loading...
      </div>
    )
  }

  return (
    <div>
      <Table className='w-full table-fixed'>
        <colgroup>
          <col className='w-4/7' />
          <col className='w-1/7' />
          <col className='w-1/7' />
          <col className='w-2/7' />
          <col className='w-2/7' />
          <col className='w-2/7' />
          {showStatusColumn ? <col className='w-1/7' /> : null}
          <col className='w-1/7' />
        </colgroup>
        <TableHeader>
          <TableRow className='bg-[#F2F4F7] hover:bg-[#F2F4F7]'>
            <TableHead className='font-mundial px-[24px] py-[12px] text-sm font-[600] text-[#002652]'>
              Name
            </TableHead>
            <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
              Participants
            </TableHead>
            <TableHead className='font-mundial w-fit text-center text-sm font-[600] text-[#002652]'>
              Unmarked
            </TableHead>
            <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
              Average AI Mark
            </TableHead>
            <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
              Average Tutor Mark
            </TableHead>
            <TableHead className='font-mundial text-sm font-[600] text-[#002652]'>
              Type
            </TableHead>
            {showStatusColumn ? (
              <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                Status
              </TableHead>
            ) : null}
            <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]' />
          </TableRow>
        </TableHeader>
        <TableBody className='[&_tr:last-child]:border-b-1'>
          {headings?.map((heading: HeadingResItem) => (
            <HeadingGroupSection
              key={heading.id}
              heading={heading}
              classId={classId}
              moduleId={moduleId}
              isExpanded={expandedHeadingIds.has(heading.id)}
              onToggle={() => handleToggleHeading(heading.id)}
              status={status}
              search={search}
              creatorType={creatorType}
              showStatusColumn={showStatusColumn}
              isPractice={isPractice}
            />
          ))}
        </TableBody>
      </Table>
      <div className='flex justify-end px-[24px] py-[16px]'>
        <Pagination
          currentPage={page}
          onPageChange={setPage}
          totalPages={meta?.total_pages || 1}
        />
      </div>
    </div>
  )
}
